import {VERDICT_EVENEMENT_VERS_PAV} from '../tokens.js';

// F4 : état d'un PAV à T = fold de ses événements (triés date asc) jusqu'à la borne haute effective.

const TYPES_PAR_ACTE = {
    lavage: ['LAVAGE', 'IMPOSSIBLE'],
    maint: ['MAINT_PREV', 'IMPOSSIBLE'],
    graffiti: ['GRAFFITI'],
};

export function nbPhotosEvenement(e) {
    let n = e.photos ? e.photos.length : 0;
    if (e.elements) {
        for (const el of e.elements) n += el.photos ? el.photos.length : 0;
    }
    return n;
}

/** Borne haute effective = min(au, at), en ignorant les valeurs vides. */
export function borneHaute({au, at}) {
    if (au && at) return au < at ? au : at;
    return au || at || null;
}

export function retenu(e, du, haute) {
    if (e.type === 'PLANIFIÉ') return false;
    // COLLECTE « déduite » du planning : jamais. Collecte simulée (lot 8) : retenue, présente seulement si le bouton est actif.
    if ((e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE') && e.source !== 'simulation') return false;
    if (du && e.date < du) return false;
    if (haute && e.date > haute) return false;
    return true;
}

function impossibleDeMaintenance(e, typologiesParPatrimoine) {
    const typologie = e.detail && e.detail.typologie;
    if (!typologie || !typologiesParPatrimoine) return false;
    const set = typologiesParPatrimoine.get(e.patrimoineId);
    return !!set && set.has(typologie);
}

// Entre deux candidats, le plus récent gagne. À date égale : `a` si prioriteA, sinon le plus tardif (heure).
function plusRecent(a, b, prioriteA) {
    if (!a) return b;
    if (!b) return a;
    if (a.date !== b.date) return a.date > b.date ? a : b;
    if (prioriteA) return a;
    return (a.heure || '') >= (b.heure || '') ? a : b;
}

/**
 * Acte `maint` : un IMPOSSIBLE n'est candidat que si `detail.typologie` appartient au set du patrimoine de
 * l'événement (`typologiesParPatrimoine` : Map<patrimoineId, Set>, cf. `index.typologiesMaintenanceParPatrimoine`).
 * Map absente, set vide ou événement sans typologie : non candidat.
 * Acte `collecte` (lot 8) : dernier COLLECTE / COLLECTE_IMPOSSIBLE <= at -> `ras` / `imp`, aucun -> `ni`. `tonnageKg` (somme des COLLECTE),
 *   `nbCollectes` (COLLECTE + COLLECTE_IMPOSSIBLE) et `nbDeclasses` sont cumulés sur la plage, quel que soit l'acte.
 * @returns {{verdict, date, evenement, elementsEnDefaut, nbPhotos, nbLavages, nbImpossibles, tonnageKg, nbCollectes, nbDeclasses, tauxRemplissage}}
 *   Les cinq premiers champs sont ceux de F4. `nbLavages` / `nbImpossibles` (comptes retenus sur la plage,
 *   indépendants de l'acte) sont ajoutés pour alimenter `kpi` (réalisés / tentatives) sans reparcourir les événements.
 */
export function etatPav(evenementsDuPav, {acte, du, au, at}, typologiesParPatrimoine = null) {
    const haute = borneHaute({au, at});
    const types = TYPES_PAR_ACTE[acte];
    let dernierLavage = null;
    let dernierMaint = null;
    let dernierImpossible = null;
    let dernierImpossibleMaint = null;
    let dernierGraffiti = null;
    let nbPhotos = 0;
    let nbLavages = 0;
    let nbImpossibles = 0;
    let dernierCollecte = null;
    let nbCollectes = 0;
    let nbDeclasses = 0;
    let tonnageKg = 0;
    let sommeTaux = 0;
    let nbTaux = 0;

    for (const e of evenementsDuPav || []) {
        if (!retenu(e, du, haute)) continue;
        if (e.type === 'LAVAGE') {
            nbLavages++;
            dernierLavage = e;
        } else if (e.type === 'IMPOSSIBLE') {
            nbImpossibles++;
            dernierImpossible = e;
            if (acte === 'maint' && impossibleDeMaintenance(e, typologiesParPatrimoine)) dernierImpossibleMaint = e;
            else if (acte === 'maint') continue; // impossible de lavage : hors acte maintenance, photos comprises
        } else if (e.type === 'MAINT_PREV') {
            dernierMaint = e;
        } else if (e.type === 'GRAFFITI') {
            dernierGraffiti = e;
        } else if (e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE') {
            // Collecte simulée (lot 8) : `nbCollectes` compte aussi les déclassés (le taux de déclassement en dépend).
            dernierCollecte = e;
            nbCollectes++;
            if (e.type === 'COLLECTE_IMPOSSIBLE') nbDeclasses++;
            else {
                tonnageKg += (e.detail && e.detail.tonnageKg) || 0;
                const taux = e.detail && e.detail.tauxRemplissage;
                if (typeof taux === 'number') { sommeTaux += taux; nbTaux++; }
            }
        }
        if (types && types.includes(e.type)) nbPhotos += nbPhotosEvenement(e);
    }

    let choisi = null;
    if (acte === 'lavage') choisi = plusRecent(dernierLavage, dernierImpossible, false);
    // À date égale le MAINT_PREV gagne (précédence EtatChecklistResolver R2 côté API).
    else if (acte === 'maint') choisi = plusRecent(dernierMaint, dernierImpossibleMaint, true);
    else if (acte === 'graffiti') choisi = dernierGraffiti;
    else if (acte === 'collecte') choisi = dernierCollecte;

    // Taux de remplissage moyen des COLLECTE de la plage (déclassés exclus), `null` sans collecte.
    const tauxRemplissage = nbTaux > 0 ? sommeTaux / nbTaux : null;

    if (!choisi) {
        return {verdict: 'ni', date: null, evenement: null, elementsEnDefaut: [], nbPhotos, nbLavages, nbImpossibles, tonnageKg, nbCollectes, nbDeclasses, tauxRemplissage};
    }

    let verdict;
    if (choisi.type === 'GRAFFITI') verdict = 'pb';
    else if (choisi.type === 'COLLECTE') verdict = 'ras';
    else if (choisi.type === 'COLLECTE_IMPOSSIBLE') verdict = 'imp';
    else if (choisi.type === 'IMPOSSIBLE') verdict = 'imp';
    else verdict = VERDICT_EVENEMENT_VERS_PAV[choisi.verdict] || 'ni';

    const elementsEnDefaut = choisi.type === 'MAINT_PREV'
        ? (choisi.elements || []).filter((el) => el.statut === 'PROBLEME').map((el) => el.libelle)
        : [];

    return {verdict, date: choisi.date, evenement: choisi, elementsEnDefaut, nbPhotos, nbLavages, nbImpossibles, tonnageKg, nbCollectes, nbDeclasses, tauxRemplissage};
}

// Cache des états par index (8 jeux de paramètres récents conservés) : le curseur temporel et les chips
// ne recalculent pas ce qui l'a déjà été.
const caches = new WeakMap();
const TAILLE_CACHE = 8;

/** Map<pavId, état> pour tous les PAV de l'index. Mémoïsé par (index, acte, du, au, at). */
export function etatsDesPav(index, {acte, du, au, at}) {
    let cache = caches.get(index);
    if (!cache) {
        cache = new Map();
        caches.set(index, cache);
    }
    const cle = `${acte}|${du || ''}|${au || ''}|${at || ''}`;
    let etats = cache.get(cle);
    if (etats) {
        cache.delete(cle);
        cache.set(cle, etats);
        return etats;
    }
    etats = new Map();
    for (const pav of index.pavs) {
        etats.set(pav.id, etatPav(index.evenementsParPav.get(pav.id), {acte, du, au, at}, index.typologiesMaintenanceParPatrimoine));
    }
    cache.set(cle, etats);
    if (cache.size > TAILLE_CACHE) cache.delete(cache.keys().next().value);
    return etats;
}

const cachesRetenus = new WeakMap(); // index -> WeakMap<tableau pavs, Map<paramètres, événements>>

/**
 * Événements retenus sur la plage pour une liste de PAV (entrée de `rse`). Mémoïsé par (index, référence de
 * `pavs`, du/au/at), LRU de 8 jeux par tableau : garder le même tableau `pavs` d'un rendu à l'autre.
 */
export function evenementsRetenus(index, pavs, {du, au, at}) {
    let parPavs = cachesRetenus.get(index);
    if (!parPavs) cachesRetenus.set(index, (parPavs = new WeakMap()));
    let cache = parPavs.get(pavs);
    if (!cache) parPavs.set(pavs, (cache = new Map()));
    const cle = `${du || ''}|${au || ''}|${at || ''}`;
    let res = cache.get(cle);
    if (res) {
        cache.delete(cle);
        cache.set(cle, res);
        return res;
    }
    const haute = borneHaute({au, at});
    res = [];
    for (const pav of pavs) {
        for (const e of index.evenementsParPav.get(pav.id) || []) {
            if (retenu(e, du, haute)) res.push(e);
        }
    }
    cache.set(cle, res);
    if (cache.size > TAILLE_CACHE) cache.delete(cache.keys().next().value);
    return res;
}
